import {
  payWithApplePayAtom,
  payWithGooglePayAtom,
  paymentMethodsAtom,
  statusAtom,
  userInfoAtom,
  verifyComponentAtom,
  verifyStepAtom,
} from '@/state/global'
import { PaymentData, PaymentDataAtom, switchedPaymentMethodAtom } from '@/state/payment'
import { API_URL, RELEASE_TAG } from '@/utils/constants'
import {
  buildFortisPaymentMethod,
  deduplicatePaymentMethods,
  fortisDuplicateMatch,
  validateFortisEvent,
} from '@/utils/fortis.utils'
import { newRelicLog } from '@/utils/newrelic'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import React, { useEffect, useRef, useState } from 'react'
import ErrorModal from '../ErrorModal'
import { VerifyStep } from '../verify/types'
import useMutationWithRedirect from '@/hooks/UseMutationWithRedirect'
import { useAuthQuery } from '@/hooks/useAuthQuery'

const HostedPage = ({
  closePage,
  hasRunningTab = false,
}: {
  closePage: (paymentMethod?: PaymentData | undefined) => void
  hasRunningTab?: boolean
}) => {
  const [payWithApplePay, setPayWithApplePay] = useAtom(payWithApplePayAtom)
  const [payWithGooglePay, setPayWithGooglePay] = useAtom(payWithGooglePayAtom)
  const [didSwitchPaymentMethod, setDidSwitchPaymentMethod] = useAtom(switchedPaymentMethodAtom)
  const [isVerifyComponentOpen, openVerifyComponent] = useAtom(verifyComponentAtom)
  const [verifyStep, setVerifyStep] = useAtom(verifyStepAtom)
  const verifyStatus = useAtomValue(statusAtom)

  const [data, setData] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const user = useAtomValue(userInfoAtom)
  const [reloadCounter, setReloadCounter] = useState(0)
  const setPaymentMethod = useSetAtom(PaymentDataAtom)
  const [paymentMethods, setPaymentMethods] = useAtom(paymentMethodsAtom)
  const [errorMessage, setErrorMessage] = useState({
    title: 'Card Declined',
    description: 'Please Try Again',
    primaryButton: { name: 'Try Again', action: () => {} },
  })
  const [openErrorModal, setOpenErrorModal] = useState(false)

  const iframeRef = useRef<any>(null)

  const handleTriggerSubmit = () => {
    setSubmitting(true)
    if (iframeRef && iframeRef.current) {
      // Since srcdoc has an opaque origin, we use '*' as the target origin
      iframeRef.current.contentWindow.postMessage('triggerSubmit', '*')
    }
  }

  const { refetch: refetch } = useAuthQuery<boolean>(['fortis-hosted-page-payload'], () => fetchData(), {
    refetchOnWindowFocus: false,
  })

  const fetchData = async () => {
    if (!user?.token) {
      throw { name: 'Unauthorized' }
    }

    const startTime = performance.now()
    const rawRes = await fetch(`${API_URL}/mobile/fortis-hosted-page-payload`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Build-Version': RELEASE_TAG,
        'X-Client-ID': user?.token ?? '',
        Platform: 'browser',
      },
    })

    const endTime = performance.now()
    const durationInMs = ((endTime - startTime) / 1000).toFixed(3)
    newRelicLog(
      {
        name: 'executedCardEntry',
        mode: 'fortis_hosted_page',
        isSuccessful: String(rawRes.ok),
        duration: Number(durationInMs),
      },
      'pageAction'
    )

    const data = await rawRes.json()

    if (!rawRes.ok) {
      throw data
    }

    if (data?.hosted_page_html) {
      const html = atob(data?.hosted_page_html)
      setData(html)
    }
    return rawRes.ok
  }

  useEffect(() => {
    refetch()
  }, [user, verifyStatus])

  const validateFortis = (event: any) => {
    const result = validateFortisEvent(event)

    if (!result.valid) {
      setErrorMessage({
        ...errorMessage,
        description: result.errorDescription,
      })
      newRelicLog(
        {
          name: 'webclip-error',
          errorDomain: 'fortis hosted page',
          errorMessage: result.errorDescription,
          errorDescription: JSON.stringify({}),
        },
        'pageAction'
      )
      setOpenErrorModal(true)
      setSubmitting(false)
      setLoading(false)
      setReloadCounter(reloadCounter + 1)
      return false
    }

    if (hasRunningTab) {
      setDidSwitchPaymentMethod(true)
    }
    return true
  }

  useEffect(() => {
    const handleMessage = (event: any) => {
      // For security, you might want to check event.origin here to ensure the message
      // is coming from the expected domain

      if (event.data.type === 'fortisReady') {
        setLoading(false)
      }
      if (event.data.type === 'fortisDone') {
        const data = event.data.data.data
        const isValid = validateFortis(data)
        if (!isValid) {
          return refetch()
        }

        const paymentMethod = buildFortisPaymentMethod(data)
        const newPayments = deduplicatePaymentMethods(paymentMethods, paymentMethod, fortisDuplicateMatch)
        localStorage.setItem('paymentMethods', JSON.stringify(newPayments))
        setPaymentMethods(newPayments)

        setPaymentMethod(paymentMethod)
        setPayWithApplePay(false)
        setPayWithGooglePay(false)
        closePage(paymentMethod)
      }
      if (event.data.type === 'fortisValidationError') {
        setSubmitting(false)
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  const handleCloseErrorModal = () => {
    setOpenErrorModal(false)
  }

  return (
    <div className="z-50 bg-white fixed inset-0 p-2 flex flex-col items-center text-black mx-auto max-w-[820px]">
      <ErrorModal
        isOpen={openErrorModal}
        close={handleCloseErrorModal}
        title={errorMessage.title}
        description={errorMessage.description}
        primaryButton={errorMessage.primaryButton}
        entities={[]}
      />
      <div className="flex justify-center relative items-center text-sm tracking-tight w-full mt-2">
        <p>PAYMENT METHODS</p>
        <button onClick={() => closePage()} className="absolute right-1 text-sm">
          Cancel
        </button>
      </div>
      <div className="mt-8 px-3 w-full">
        <h1 className="uppercase text-2xl">Add Card</h1>
        <p className="text-sm text-gray-600 mt-4 leading-tight">
          Your card info is completely encrypted and tokenized, and never stored in Union.
        </p>
      </div>
      {loading ? (
        <div className="animate-pulse h-full w-full">
          <div className="w-full h-12 bg-gray-300 rounded-md my-1" />
          <div className="w-full h-12 bg-gray-300 rounded-md my-1" />
          <div className="w-full h-12 bg-gray-300 rounded-md my-1" />
          <div className="w-full h-12 bg-gray-300 rounded-md my-1" />
          <div className="w-full h-12 bg-gray-300 rounded-md my-1" />
        </div>
      ) : null}
      <iframe srcDoc={data} ref={iframeRef} width="100%" height="60%" title="Fortis Checkout" key={reloadCounter}></iframe>
      <button
        className="h-[75px] py-7 text-center bg-primary-blue w-full flex items-center justify-center fixed bottom-0 mx-auto max-w-[820px]"
        onClick={handleTriggerSubmit}
        disabled={loading || submitting}
      >
        {submitting ? (
          <span className="text-white">
            <svg
              width="45"
              height="50"
              viewBox="0 0 145 50"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="animate-spin"
            >
              <path
                d="M121.408 0C109.718 0 103.134 9.99898 99.194 13.9392C90.239 22.8943 88.1307 2.0699 80.5854 2.0699H72.3979H42.7182V24.1889C42.7182 29.7667 40.3797 32.9137 35.7026 32.9137C31.7444 32.9137 29.6771 30.4831 29.6771 26.3484V2.06478H0V30.3935C0 43.2556 7.82161 50 23.0273 50C34.7175 50 41.3008 40.001 45.241 36.0608C54.1961 27.1057 56.3044 47.9327 63.8497 47.9327H101.717V25.8111C101.717 20.2333 104.055 17.0863 108.732 17.0863C112.691 17.0863 114.758 19.5169 114.758 23.6516V47.9352H144.438V19.6065C144.43 6.74445 136.608 0 121.408 0Z"
                fill="white"
              />
            </svg>
          </span>
        ) : (
          <span className="text-white">Save</span>
        )}
      </button>
    </div>
  )
}

export default HostedPage
